/**
 * Environment utilities for managing environment-specific configurations.
 */

/**
 * Get the current environment origin (hostname:port).
 * Strips protocol (http/https) to create a consistent key.
 *
 * Examples:
 * - http://localhost:3000 → "localhost:3000"
 * - https://preview.vercel.app → "preview.vercel.app"
 * - https://app.example.com → "app.example.com"
 *
 * @returns Current origin without protocol
 */
export function getCurrentOrigin(): string {
  if (typeof window === 'undefined') {
    throw new Error(
      'getCurrentOrigin can only be called in browser environment',
    )
  }

  // Remove protocol from origin
  return window.location.origin.replace(/^https?:\/\//, '')
}

/**
 * Get a localStorage key that's specific to the current environment.
 * This ensures each environment (localhost, preview, production) has isolated storage.
 *
 * @param baseKey - The base key name
 * @returns Environment-specific storage key
 *
 * @example
 * ```typescript
 * const key = getEnvironmentStorageKey('doc-url')
 * // Returns: "contour-localhost:3000-doc-url" on localhost
 * // Returns: "contour-preview.vercel.app-doc-url" on preview
 * ```
 */
export function getEnvironmentStorageKey(baseKey: string): string {
  // Safe to call during module evaluation - will only be used in browser context
  if (typeof window === 'undefined') {
    // Return a placeholder during SSR - this won't be used since localStorage is browser-only
    return `contour-ssr-${baseKey}`
  }
  const origin = getCurrentOrigin()
  return `contour-${origin}-${baseKey}`
}
